import React, { useState, useRef, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { 
  DndContext, 
  closestCenter,
  useSensors,
  useSensor,
  PointerSensor
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { 
  DotsVerticalIcon, 
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeOffIcon,
  PlusIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  MenuAlt4Icon,
  DocumentTextIcon,
  BookOpenIcon,
  ClipboardListIcon,
  DocumentIcon,
  UserGroupIcon
} from '@heroicons/react/outline';
import DropdownPortal from './DropdownPortal';
import Lesson from './Lesson';
import Quiz from './Quiz';
import Resource from './Resource';
import Group from './Group';
import GroupModal from './GroupModal';
import ResourceModal from './ResourceModal';
import PermissionGuard from '../../../../../common/PermissionGuard';

const Section = ({ 
  section, 
  isExpanded,
  onExpand,
  onAddLesson, 
  onAddQuiz, 
  onAddResource,
  onAddGroup,
  onStatusChange, 
  onDelete, 
  onEdit,
  onLessonStatusChange,
  onLessonDelete,
  onLessonEdit,
  onQuizStatusChange,
  onQuizDelete,
  onQuizEdit,
  onResourceStatusChange,
  onResourceDelete,
  onResourceEdit,
  onGroupStatusChange,
  onGroupDelete,
  onGroupEdit,
  onItemsReorder,
}) => {
  const [showActions, setShowActions] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  // إغلاق القائمة عند النقر خارجها
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showActions && 
        buttonRef.current && 
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowActions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActions]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // تقليل المسافة المطلوبة لبدء السحب
      activationConstraint: {
        distance: 5, // بكسل
      },
    })
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id && section.items) {
      onItemsReorder(active.id, over.id);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 mb-4 overflow-visible cursor-default
        ${isDragging ? 'opacity-50' : ''}`}
    >
      {/* رأس القسم */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-l from-blue-50/80 to-white/80 
        dark:from-gray-700/80 dark:to-gray-800/80 border-b border-gray-100 dark:border-gray-700 overflow-visible">
        <div className="flex items-center gap-3 overflow-visible">
          {/* مقبض السحب */}
          <div className="relative group overflow-visible">
            <div
              {...attributes}
              {...listeners}
              className="cursor-move text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <MenuAlt4Icon className="w-4 h-4" />
            </div>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              اسحب لإعادة الترتيب
            </div>
          </div>

          {/* زر التوسيع */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onExpand();
            }}
            className="text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-5 h-5" />
            ) : (
              <ChevronRightIcon className="w-5 h-5" />
            )}
          </button>

          <div className="relative group overflow-visible">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <DocumentTextIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              {section.title}
            </h3>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              عنوان القسم
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 overflow-visible">
          {/* إضافة درس */}
          <div className="relative group overflow-visible">
            <button
              onClick={onAddLesson}
              className="text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-500
                transition-colors duration-200"
            >
              <BookOpenIcon className="w-5 h-5" />
            </button>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              إضافة درس جديد
            </div>
          </div>

          {/* إضافة اختبار */}
          <div className="relative group overflow-visible">
            <button
              onClick={onAddQuiz}
              className="text-purple-500 dark:text-purple-400 hover:text-purple-600 dark:hover:text-purple-500
                transition-colors duration-200"
            >
              <ClipboardListIcon className="w-5 h-5" />
            </button>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              إضافة اختبار جديد
            </div>
          </div>

          {/* مؤشر حالة النشر */}
          <div className="relative group overflow-visible">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onStatusChange(section.id);
              }}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
            >
              {section.status === 'published' ? (
                <EyeIcon className="w-5 h-5 text-green-500" />
              ) : (
                <EyeOffIcon className="w-5 h-5" />
              )}
            </button>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              {section.status === 'published' ? 'إخفاء القسم' : 'نشر القسم'}
            </div>
          </div>

          {/* قائمة الإجراءات */}
          <div className="relative overflow-visible">
            <div className="relative group overflow-visible">
              <button
                ref={buttonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowActions(!showActions);
                }}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
              >
                <DotsVerticalIcon className="w-5 h-5" />
              </button>
              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                المزيد من الخيارات
              </div>
            </div>

            {showActions && (
              <DropdownPortal 
                buttonRef={buttonRef}
                onClose={() => setShowActions(false)}
                ref={dropdownRef}
              >
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg 
                  border border-gray-200 dark:border-gray-700 w-36 overflow-visible">
                  {/* إضافة حقيبة تعليمية */}
                  <div className="relative group">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowActions(false);
                        setShowResourceModal(true);
                      }}
                      className="w-full text-right px-3 py-2 text-sm text-gray-700 dark:text-gray-300 
                        hover:bg-gray-100 dark:hover:bg-gray-700/50 flex items-center gap-2"
                    >
                      <DocumentIcon className="w-4 h-4" />
                      إضافة حقيبة
                    </button>
                    <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                      إضافة حقيبة تعليمية PDF
                    </div>
                  </div>

                  {/* إضافة قروب */}
                  <div className="relative group">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowActions(false);
                        onAddGroup();
                      }}
                      className="w-full text-right px-3 py-2 text-sm text-gray-700 dark:text-gray-300 
                        hover:bg-gray-100 dark:hover:bg-gray-700/50 flex items-center gap-2"
                    >
                      <UserGroupIcon className="w-4 h-4" />
                      إضافة قروب
                    </button>
                    <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                      إضافة رابط قروب تعليمي
                    </div>
                  </div>

                  <div className="h-px bg-gray-200 dark:bg-gray-700" />

                  {/* تعديل */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowActions(false);
                      onEdit(section.id);
                    }}
                    className="w-full text-right px-3 py-2 text-sm text-gray-700 dark:text-gray-300 
                      hover:bg-gray-100 dark:hover:bg-gray-700/50 flex items-center gap-2"
                  >
                    <PencilIcon className="w-4 h-4" />
                    تعديل
                  </button>

                  {/* حذف */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowActions(false);
                      onDelete(section.id);
                    }}
                    className="w-full text-right px-3 py-2 text-sm text-red-600 dark:text-red-400 
                      hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center gap-2"
                  >
                    <TrashIcon className="w-4 h-4" />
                    حذف
                  </button>
                </div>
              </DropdownPortal>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 space-y-3 bg-white/40 dark:bg-gray-800/40 overflow-visible">
          {(section.items || []).length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              لا يوجد محتوى في هذا القسم بعد
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={(section.items || []).map(item => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3 overflow-visible">
                  {(section.items || []).map(item => {
                    // عرض عناصر القسم
                    return (
                    <div key={item.id}>
                      {item.type === 'lesson' || item.type === 'video' || item.type === 'video_url' || item.type === 'video_file' || item.type === 'text' || item.type === 'file' || item.type === 'video_embed' ? (
                        <Lesson
                          lesson={item}
                          onStatusChange={() => onLessonStatusChange(item.id)}
                          onDelete={() => {
                            // حذف عنصر من القسم
                            onLessonDelete(item);
                          }}
                          onEdit={() => onLessonEdit(item)}
                        />
                      ) : item.type === 'quiz' ? (
                        <Quiz
                          quiz={item}
                          onStatusChange={() => onQuizStatusChange(item.id)}
                          onDelete={() => onQuizDelete(item.id)}
                          onEdit={() => onQuizEdit(item.id)}
                        />
                      ) : item.type === 'resource' ? (
                        <Resource
                          resource={item}
                          onStatusChange={() => onResourceStatusChange(item.id)}
                          onDelete={() => onResourceDelete(item.id)}
                          onEdit={() => onResourceEdit(item)}
                        />
                      ) : item.type === 'group' ? (
                        <Group
                          group={item}
                          onStatusChange={() => onGroupStatusChange(item.id)}
                          onDelete={() => onGroupDelete(item.id)}
                          onEdit={() => onGroupEdit(item.id)}
                        />
                      ) : null}
                    </div>
                  )})}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}
      {/* النوافذ المنبثقة */}
      <ResourceModal
        isOpen={showResourceModal}
        onClose={() => setShowResourceModal(false)}
        onSelect={onAddResource}
      />
    </div>
  );
};

export default Section;
